import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { PageHeader, Table } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";

// The console's overview: every internal area, what's waiting where a list
// endpoint exists, and how each decision is controlled on the server.

interface Area {
  to: string;
  name: string;
  waiting: string;
  control: string;
}

export function StaffHome() {
  const ep = useEndpoints();
  const review = useQuery({ queryKey: staffKeys.reviewQueue, queryFn: () => ep.staff.review.queue() });
  const disputes = useQuery({ queryKey: staffKeys.disputeCount, queryFn: () => ep.staff.disputes.list("open") });
  const ops = useQuery({ queryKey: staffKeys.providerOps, queryFn: () => ep.staff.providerOperations.unresolved() });
  const matrix = useQuery({ queryKey: staffKeys.clientMatrix, queryFn: () => ep.staff.clientMatrix.list() });
  const quarantines = useQuery({ queryKey: staffKeys.quarantines, queryFn: () => ep.staff.reconciliation.quarantines("open") });

  const count = (n: number | undefined, more = false) => (n === undefined ? "…" : `${n}${more ? "+" : ""}`);
  const byKind = (k: string) => review.data?.items.filter((i) => i.kind === k).length;
  const areas: Area[] = [
    {
      to: "/staff/review",
      name: "Review queue",
      waiting: review.data
        ? `${byKind("config_version")} config · ${byKind("creative_version")} creative · ${byKind("appeal")} appeals`
        : "…",
      control: "One reviewer; appeals need step-up and a different reviewer from the rejection",
    },
    {
      to: "/staff/disputes",
      name: "Statement disputes",
      waiting: `${count(disputes.data?.disputes.length, !!disputes.data?.next_cursor)} open`,
      control: "Step-up; an uphold moving more than 100 needs two distinct reviewers",
    },
    {
      to: "/staff/provider-operations",
      name: "Provider operations",
      waiting: `${count(ops.data?.operations.length)} unresolved or flagged`,
      control: "Step-up; failed and double-payment need two distinct reviewers",
    },
    {
      to: "/staff/debt-cases",
      name: "Publisher debt cases",
      waiting: "Open by id (no list endpoint)",
      control: "Two distinct reviewers above 100 or when escalated; appeal deciders can't be the adjudicators",
    },
    { to: "/staff/write-offs", name: "Receivable write-offs", waiting: "Open by case id (no list endpoint)", control: "One reviewer, reason required" },
    {
      to: "/staff/reconciliation",
      name: "Reconciliation quarantines",
      waiting: `${count(quarantines.data?.quarantines.length)} quarantined`,
      control: "Step-up; clearing needs two distinct reviewers agreeing on the resolution",
    },
    { to: "/staff/safety", name: "Safety findings", waiting: "No list endpoint", control: "One reviewer, reason required" },
    { to: "/staff/breaker", name: "CPE circuit breaker", waiting: "State from GET /v1/status", control: "One reviewer, reason required" },
    {
      to: "/staff/client-matrix",
      name: "Approved client matrix",
      waiting: matrix.data ? `${matrix.data.entries.length} entries, ${matrix.data.entries.filter((e) => e.status !== "validated").length} suspended` : "…",
      control: "Step-up on every change; evidence required",
    },
  ];

  return (
    <div className="stack-lg">
      <PageHeader title="Staff console" description="Internal review and operations for platform reviewers. Every action is recorded against your account." />
      <Table
        caption="Areas"
        columns={[
          { key: "area", header: "Area", cell: (a: Area) => <Link to={a.to}>{a.name}</Link> },
          { key: "waiting", header: "Waiting", cell: (a: Area) => a.waiting },
          { key: "control", header: "Server-side control", cell: (a: Area) => <span className="small">{a.control}</span> },
        ]}
        rows={areas}
        rowKey={(a) => a.to}
      />
    </div>
  );
}

export function StaffNotFound() {
  return (
    <div className="stack">
      <PageHeader title="Not found" />
      <p>
        There's no staff screen at this address. <Link to="/staff">Back to the overview</Link>.
      </p>
    </div>
  );
}
